import { DatePicker, Flex, Form, FormProps, Input, Radio, SelectProps } from 'antd';
import dayjs from 'dayjs';
import { t } from 'i18next';

import AntdSelect from '@/components/general/AntdSelect/AntdSelect';
import CompanySelectField from '@/components/general/CompanySelectField';
import InfoTooltip from '@/components/general/InfoTooltip';
import LinkTextField from '@/components/general/LinkTextField';
import PrioritySelect from '@/components/general/PrioritySelect';
import { MACHINES_URLS } from '@/constants/urls';
import { TicketContactTypeString } from '@/model/Ticket/TicketConstants';
import { RESOURCES } from '@/Resources';

interface TicketFormGeneralInfoProps {
  formProps: FormProps;
  formInitialValues?: any;
  edit: boolean;
  companySelectProps: SelectProps;
  subCategorySelectProps: SelectProps;
  machineSelectProps: SelectProps;
  refTicketsSelectProps: SelectProps;
  usersSelectProps: SelectProps;
  subStatusSelectProps: SelectProps;
}

const TicketFormGeneralInfo = (props: TicketFormGeneralInfoProps) => {
  const {
    formProps,
    companySelectProps,
    subCategorySelectProps,
    machineSelectProps,
    refTicketsSelectProps,
    usersSelectProps,
    subStatusSelectProps,
    formInitialValues,
    edit,
  } = props;
  const machine = Form.useWatch('movil', formProps.form);
  const reference = Form.useWatch('referencia', formProps.form);

  return (
    <>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Subcategoría')}
          name={'subcategoria'}
          className="TicketListFilterRow"
          rules={[
            {
              required: true,
              message: t('La subcategoría es requerida'),
            },
          ]}>
          <AntdSelect {...subCategorySelectProps} allowClear showSearch />
        </Form.Item>

        <Form.Item
          label={
            <>
              {t('Título')}
              <InfoTooltip
                tooltipText={t('Se autocompletara con el nombre de la subcategoría')}
                iconClassName="TicketTitleInfoIcon"
              />
            </>
          }
          name={'title'}>
          <Input />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        {/* Mejora: CompanySelectField encapsula el patrón select + botón info repetido en 4 forms */}
        <CompanySelectField
          name="empresa"
          label={t('Empresa')}
          formProps={formProps}
          selectProps={companySelectProps}
        />
        <Form.Item label={t('Móvil')} name={'movil'}>
          <AntdSelect {...machineSelectProps} allowClear showSearch />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        <Form.Item label={t('Referencia')}>
          <Flex vertical gap={4}>
            <Form.Item noStyle name={'referencia'}>
              <AntdSelect {...refTicketsSelectProps} allowClear showSearch />
            </Form.Item>
            {reference && (
              <LinkTextField
                label={t('Ver ticket de referencia')}
                to={`/${RESOURCES.TICKETS}/edit/${reference}`}
                local
                newTab
              />
            )}
          </Flex>
        </Form.Item>
        <Form.Item
          label={t('Asignado a')}
          name={'assigned_to'}
          className="TicketListFilterRow"
          rules={[
            {
              required: true,
              message: t('Se requiere asignar un responsable'),
            },
          ]}>
          <AntdSelect options={usersSelectProps.options} allowClear showSearch />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        <Form.Item label={t('Prioridad')} name={'priority'} className="TicketListFilterRow">
          <PrioritySelect />
        </Form.Item>
        <Form.Item label={t('Subestado')} name={'substatus'} className="TicketListFilterRow">
          <AntdSelect {...subStatusSelectProps} allowClear showSearch />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Tipo')}
          name={'contact_type'}
          rules={[
            {
              required: true,
              message: t('El {{field}} es requerido', { field: t('Tipo') }),
            },
          ]}>
          <Radio.Group
            className="TicketFormTypeRadioGroup"
            options={Object.values(TicketContactTypeString).map((v, index) => ({
              label: t(v),
              value: index + 1,
            }))}
          />
        </Form.Item>
        <Form.Item
          label={
            <>
              {t('Próximo recordatorio')}
              <InfoTooltip
                tooltipText={t('Se recibirá una notificación en la fecha seleccionada')}
              />
            </>
          }
          name={'reminder_date'}
          getValueProps={value => ({
            value: value ? dayjs(value) : undefined,
          })}>
          <DatePicker
            className="TicketFormDatePicker"
            format={'L'}
            placeholder={t('Seleccionar una fecha')}
          />
        </Form.Item>
      </div>
      <div className="TicketFormItemsContainer">
        <Form.Item
          label={t('Fecha de resolución del problema del cliente')}
          name={'finish_problem_date'}
          getValueProps={value => ({
            value: value ? dayjs(value) : undefined,
          })}>
          <DatePicker
            className="TicketFormDatePicker"
            format={'L'}
            placeholder={t('Seleccionar una fecha')}
            onChange={() => {
              // Resolución: limpia el error manual apenas el usuario modifica la fecha.
              formProps.form?.setFields([
                {
                  name: 'finish_problem_date',
                  errors: [],
                },
              ]);
            }}
          />
        </Form.Item>
      </div>
      {/* Links adicionales */}
      {edit && formInitialValues?.movil && (
        <div style={{ marginBottom: 16 }}>
          <div>{t('Links adicionales de máquina')}</div>
          <Flex className="TicketShowDataLinks" gap={24}>
            <LinkTextField
              label={t('Admin móvil')}
              to={`${MACHINES_URLS.ADMIN_MOVIL}/${machine}`}
              admin
            />
            <LinkTextField label={t('Info de móvil')} to={`/machines/detail/${machine}`} platform />
            <LinkTextField
              label={t('Ver historial de trabajos')}
              to={`/machines/works/${machine}`}
              platform
            />
            <LinkTextField
              label={t('Ver historial por hora')}
              to={`/machines/history/${machine}`}
              platform
            />
          </Flex>
        </div>
      )}
    </>
  );
};

export default TicketFormGeneralInfo;
